import { useState, useRef, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";

// Общий тост-баннер киоска — раньше был продублирован в ListDoc.jsx и
// EditManyDocs.jsx (один и тот же таймер/стейт/разметка). Вынесен сюда,
// чтобы третье место (BlankSheet.jsx — тост при упоре в лимит бумаги) не
// стало третьей копией.
const TOAST_DURATION = 2400;

export function useKioskToast(duration = TOAST_DURATION) {
    const [toastMsg, setToastMsg] = useState(null);
    const [toastTone, setToastTone] = useState("danger");
    const toastTimerRef = useRef(null);

    useEffect(() => {
        return () => clearTimeout(toastTimerRef.current);
    }, []);

    // useCallback — стабильная ссылка, чтобы showToast можно было класть в
    // зависимости useEffect без риска зациклить его на каждый рендер (сам
    // хук раньше отдавал новую функцию каждый раз, ловушка для любого
    // эффекта, который вызывает showToast как побочный эффект, а не по клику).
    // tone — необязательный второй аргумент ("danger" по умолчанию, как было
    // всегда; "accent" — для нейтральных/положительных сообщений).
    const showToast = useCallback((msg, tone = "danger") => {
        clearTimeout(toastTimerRef.current);
        setToastMsg(msg);
        setToastTone(tone);
        toastTimerRef.current = setTimeout(() => setToastMsg(null), duration);
    }, [duration]);

    return { toastMsg, toastTone, showToast };
}

// position="bottom" — всплывает НАД якорем (нижний край якоря — верх тоста),
// как в ListDoc (панель выбора внизу экрана, тост всплывает вверх).
// position="top" — раскрывается ПОД якорем (как в EditManyDocs, под табами).
// Якорь (родитель) должен быть position:relative — гоча framer-motion/Tailwind
// transform (см. CLAUDE.md): x задаётся ТОЛЬКО через framer-motion, поэтому
// на самом тосте нет Tailwind translate-класса.
const POSITION_CLASS = {
    bottom: "absolute left-1/2 bottom-[calc(100%+1.2vh)]",
    top: "absolute left-1/2 top-[calc(100%+0.8vh)] z-30",
};

// tone="danger" (по умолчанию) — предупреждения/лимиты (тот же красный, что
// и раньше). tone="accent" — нейтральное/успешное сообщение (например «QR
// обновлён»): красный здесь читался бы как ошибка, хотя это не она.
const TONE_CLASS = {
    danger: "bg-[#C2543E] shadow-[0_0.7vh_2vh_rgba(194,84,62,.35)]",
    accent: "bg-[#246CE2] shadow-[0_0.7vh_2vh_rgba(36,108,226,.35)]",
};

export function KioskToast({ message, position = "bottom", tone = "danger", className = "" }) {
    const fromY = position === "top" ? -10 : 12;
    return (
        <AnimatePresence>
            {message && (
                <motion.div
                    initial={{ opacity: 0, y: fromY, x: "-50%" }}
                    animate={{ opacity: 1, y: 0, x: "-50%" }}
                    exit={{ opacity: 0, y: fromY, x: "-50%" }}
                    transition={{ duration: 0.25 }}
                    className={`${POSITION_CLASS[position]} ${TONE_CLASS[tone]} text-white rounded-full py-[1vh] px-[2vw] text-[1.4vh] font-bold whitespace-nowrap ${className}`}
                >
                    {message}
                </motion.div>
            )}
        </AnimatePresence>
    );
}
